import { Container, Sprite, Texture, utils } from 'pixi.js';

import { Shared } from '../../App';
import { DisplayUtils } from '../../utils/DisplayUtils';
import { BlinkComponent } from '../components/BlinkComponent';
import { GHOST_COLOR, GHOST_COLOR_MIX } from '../Game';
import { PhysicsCollisionGroup } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { PhysicsShapeData, PhysicsUtils } from '../physics/PhysicsUtils';
import { ReplayBodyComponent } from '../replay';
import { BaseEntity } from './BaseEntity';

export interface ChassisData
{
    id: string,
    tier: number,
    available?: boolean,
    type: 'bike' | 'car',
    mass: number,
    seatX: number,
    seatY: number,
    wheelLeftX: number,
    wheelLeftY: number,
    wheelRightX: number,
    wheelRightY: number,
    spriteOffsetX: number,
    spriteOffsetY: number,
    sprites: {id: string, tint?: string}[],
    shapes: PhysicsShapeData[],
    ghost?: boolean,
    coinPrice?: number,
    diamondPrice?: number,
    unlocked?: boolean;
    stats: {
        speed: number,
        flipSpeed: number,
        handling: number,
        bounce: number
    }
}

export class ChassisEntity extends BaseEntity<ChassisData>
{
    public physics: PhysicsComponent;
    public replay: ReplayBodyComponent;
    public blink: BlinkComponent;
    private seatPos?: Sprite;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.replay = this.addComponent(ReplayBodyComponent);
        this.blink = this.addComponent(BlinkComponent);
    }

    public build(data: ChassisData): void
    {
        this.data = data;
        this.view.removeChildren();
        this.physics.clearParts();
        ChassisEntity.mountSprite(this.view, data);

        const body = PhysicsUtils.createBody({
            mass: data.ghost ? 0 : data.mass,
        }, ...data.shapes);

        PhysicsUtils.setupShapes(body.shapes, {
            group: data.ghost ? 0 : PhysicsCollisionGroup.VEHICLE,
            mask: data.ghost ? 0 : PhysicsCollisionGroup.LEVEL,
            sensor: data.ghost,
        });

        this.physics.addParts(body);

        if (Shared.game.debug)
        {
            if (!this.seatPos) this.seatPos = new Sprite(Texture.WHITE);
            this.seatPos.scale.set(0.8);
            this.seatPos.anchor.set(0.5);
            this.seatPos.x = data.seatX;
            this.seatPos.y = data.seatY;
            this.view.addChild(this.seatPos);
        }
    }

    public updateColor(color: string): void
    {
        for (let i = 0; i < this.view.children.length; i++)
        {
            const spr = this.view.children[i] as Sprite;
            const sprData = this.data?.sprites[i];

            if (!sprData || !sprData.tint)
            {
                spr.tint = this.data?.ghost && GHOST_COLOR_MIX ? GHOST_COLOR : 0xFFFFFF;
                continue;
            }

            const tint = utils.string2hex(color);
            const mixed = DisplayUtils.mixColors(tint, GHOST_COLOR, GHOST_COLOR_MIX);

            spr.tint = this.data?.ghost ? mixed : tint;
        }
    }

    public static mountSprite(container: Container, data: ChassisData, color = '#FFFFFE'): Container
    {
        for (const spriteData of data.sprites)
        {
            const sprite = Sprite.from(Shared.assets.getTexture(spriteData.id));

            sprite.anchor.set(0.5);
            sprite.x = data.spriteOffsetX;
            sprite.y = data.spriteOffsetY;
            sprite.tint = spriteData.tint ? utils.string2hex(color) : 0xFFFFFF;
            container.addChild(sprite);
        }

        return container;
    }
}
